import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  Calendar,
  Trophy,
  LogOut,
  Download,
  Star,
  Target,
  Zap,
  Crown,
  Gift,
  Settings,
  QrCode,
  ArrowRight,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Play,
  Heart,
  Users,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import { EventCard } from '../components/EventCard';
import { Certificate } from '../components/Certificate';

// ── Foundation config ────────────────────────────────────────────
// Set to a YouTube video ID (e.g. "dQw4w9WgXcQ") to enable the mission video modal.
const FOUNDATION_VIDEO_ID = '';

const VOLUNTEER_QUOTES = [
  {
    quote: "The best way to find yourself is to lose yourself in the service of others.",
    author: "Mahatma Gandhi",
    role: "Leader & Peace Activist"
  },
  {
    quote: "We rise by lifting others. Every act of service — no matter how small — changes the world.",
    author: "Robert Ingersoll",
    role: "Humanitarian"
  },
  {
    quote: "Volunteers do not necessarily have the time; they just have the heart.",
    author: "Elizabeth Andrew",
    role: "Author"
  },
  {
    quote: "No one has ever become poor by giving. Generosity is the seed of greatness.",
    author: "Anne Frank",
    role: "Diarist & Symbol of Hope"
  },
  {
    quote: "It's not about what you have — it's about what you give. That is your legacy.",
    author: "Hammad Foundation",
    role: "Our Core Belief"
  },
  {
    quote: "Alone we can do so little; together we can do so much. You are the change.",
    author: "Helen Keller",
    role: "Activist & Lecturer"
  },
  {
    quote: "To the world you may be one person, but to one person you may be the world.",
    author: "Dr. Seuss",
    role: "Author"
  },
  {
    quote: "Community is built one act of kindness at a time. Thank you for being here.",
    author: "Hammad Foundation",
    role: "Our Promise to You"
  }
];

const LEVEL_THRESHOLDS: Record<string, { min: number; max: number; next: string }> = {
  bronze:   { min: 0,   max: 49,  next: 'Silver' },
  silver:   { min: 50,  max: 149, next: 'Gold' },
  gold:     { min: 150, max: 299, next: 'Platinum' },
  platinum: { min: 300, max: 499, next: 'Diamond' },
  diamond:  { min: 500, max: Infinity, next: '' }
};

// ── Types ────────────────────────────────────────────────────────
interface MemberData {
  id: number;
  memberId?: string;
  member_id?: string;
  fullName?: string;
  full_name?: string;
  email: string;
  department?: string;
  university?: string;
  totalPoints?: number;
  total_points?: number;
  achievementLevel?: string;
  achievement_level?: string;
  rankPosition?: number | null;
  rank_position?: number | null;
  qrCode?: string;
  qr_code?: string;
  profileImage?: string;
  profile_image?: string;
  phone?: string;
  cnic?: string;
  student_id?: string;
}

interface Event {
  id: number;
  title: string;
  description?: string;
  event_date: string;
  event_time?: string;
  location?: string;
  category?: string;
  points: number;
  capacity?: number;
  brochure_url?: string;
  brochureUrl?: string;
  status: 'upcoming' | 'ongoing' | 'completed';
  attendanceCount?: number;
  isRegistered?: boolean;
  hasAttended?: boolean;
}

interface AttendanceRecord {
  event_id: number;
  event_title: string;
  event_date: string;
  points_earned: number;
  has_attended: boolean;
  certificate_template?: string;
}

// ── Animated counter hook ────────────────────────────────────────
function useAnimatedCounter(target: number, duration = 1200): number {
  const [value, setValue] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === 0) { setValue(0); return; }
    startRef.current = null;
    let frame: number;
    const animate = (timestamp: number) => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out-cubic
      setValue(Math.floor(eased * target));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return value;
}

// ── Helpers ──────────────────────────────────────────────────────
function getTimeGreeting(name: string): { greeting: string; subtitle: string } {
  const hour = new Date().getHours();
  const firstName = name.split(' ')[0];

  if (hour >= 5 && hour < 12) {
    return {
      greeting: `Good morning, ${firstName}! ☀️`,
      subtitle: "A new day — a new chance to make a difference."
    };
  } else if (hour >= 12 && hour < 17) {
    return {
      greeting: `Good afternoon, ${firstName}! 🌿`,
      subtitle: "You're in the middle of something great. Keep going."
    };
  } else if (hour >= 17 && hour < 21) {
    return {
      greeting: `Good evening, ${firstName}! 🌙`,
      subtitle: "Thank you for everything you do for the community."
    };
  } else {
    return {
      greeting: `Welcome back, ${firstName}! 🌟`,
      subtitle: "Every moment of service counts — and so do you."
    };
  }
}

function getProgressToNextLevel(points: number, level: string): { percent: number; pointsNeeded: number; nextLevel: string } {
  const key = level.toLowerCase();
  const threshold = LEVEL_THRESHOLDS[key] ?? LEVEL_THRESHOLDS.bronze;
  if (!threshold.next) return { percent: 100, pointsNeeded: 0, nextLevel: '' };
  const range = threshold.max - threshold.min + 1;
  const earned = points - threshold.min;
  const percent = Math.min(Math.round((earned / range) * 100), 100);
  const pointsNeeded = threshold.max + 1 - points;
  return { percent, pointsNeeded: Math.max(pointsNeeded, 0), nextLevel: threshold.next };
}

// ────────────────────────────────────────────────────────────────
const MemberDashboardNew: React.FC = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberData | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceRecord[]>([]);
  const [registeredEvents, setRegisteredEvents] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showCertificateModal, setShowCertificateModal] = useState<boolean>(false);
  const [selectedCertificate, setSelectedCertificate] = useState<AttendanceRecord | null>(null);
  const [voucherUrl, setVoucherUrl] = useState<string | null>(null);
  const [voucherLoading, setVoucherLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'events' | 'achievements' | 'certificates'>('events');

  // Quote rotator state
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [quoteVisible, setQuoteVisible] = useState(true);
  const quoteIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Video modal
  const [showVideoModal, setShowVideoModal] = useState(false);

  // ── Data loading ───────────────────────────────────────────────
  useEffect(() => {
    loadMemberData();
    loadEvents();
    loadAttendanceHistory();
    fetchMemberProfile();
    // eslint-disable-next-line
  }, []);

  // Quote auto-rotation
  const cycleQuote = useCallback((direction: 'next' | 'prev' = 'next') => {
    setQuoteVisible(false);
    setTimeout(() => {
      setQuoteIndex(i =>
        direction === 'next'
          ? (i + 1) % VOLUNTEER_QUOTES.length
          : (i - 1 + VOLUNTEER_QUOTES.length) % VOLUNTEER_QUOTES.length
      );
      setQuoteVisible(true);
    }, 300);
  }, []);

  useEffect(() => {
    quoteIntervalRef.current = setInterval(() => cycleQuote('next'), 7000);
    return () => {
      if (quoteIntervalRef.current) clearInterval(quoteIntervalRef.current);
    };
  }, [cycleQuote]);

  const handleQuoteNav = (dir: 'next' | 'prev') => {
    if (quoteIntervalRef.current) clearInterval(quoteIntervalRef.current);
    cycleQuote(dir);
    quoteIntervalRef.current = setInterval(() => cycleQuote('next'), 7000);
  };

  // ── API calls ──────────────────────────────────────────────────
  const fetchVoucher = async (memberId: number) => {
    try {
      const response = await axios.get(`/members/${memberId}/voucher`);
      setVoucherUrl(response.data.data?.voucher_url || null);
    } catch {
      setVoucherUrl(null);
    }
  };

  const handleRequestCard = async () => {
    const userData = localStorage.getItem('user');
    if (!userData) return;
    const user = JSON.parse(userData);
    const memberId = user.id;

    setVoucherLoading(true);
    try {
      const response = await axios.get(`/members/${memberId}/voucher`);
      const url = response.data.data?.voucher_url;
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.info('Your card is not ready yet. Please check back later.');
      }
    } catch {
      toast.error('Unable to fetch your card. Please try again.');
    } finally {
      setVoucherLoading(false);
    }
  };

  const loadMemberData = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedData = JSON.parse(userData);
      setMember(parsedData as MemberData);
      if (parsedData.id) fetchVoucher(parsedData.id);
    } else {
      navigate('/member/login');
    }
    setLoading(false);
  };

  const fetchMemberProfile = async () => {
    try {
      const response = await axios.get('/auth/me');
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setMember(response.data.user as MemberData);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await axios.get('/events');
      const eventsData = response.data.data ?? [];
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    }
  };

  const loadAttendanceHistory = async () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData) as MemberData;
        const response = await axios.get(`/attendance/member/${user.id}`);
        const attendanceData = response.data.data?.attendance ?? response.data.attendance ?? [];
        setAttendanceHistory(Array.isArray(attendanceData) ? attendanceData : []);

        if (response.data.data?.statistics) {
          const stats = response.data.data.statistics;
          const totalPoints = parseInt(stats.total_points || '0');
          setMember(prev => prev ? { ...prev, totalPoints: totalPoints, total_points: totalPoints } : null);
        }
      }
    } catch (error) {
      console.error('Error loading attendance:', error);
      setAttendanceHistory([]);
    }
  };

  const handleRegisterEvent = async (eventId: number) => {
    try {
      await axios.post(`/events/${eventId}/register`, {});
      setRegisteredEvents(prev => [...prev, eventId]);
      toast.success('Successfully registered for event!');
    } catch (error: any) {
      toast.error(
        (error && error.response && error.response.data && error.response.data.error)
        || 'Failed to register'
      );
    }
  };

  const handleRSVP = async (eventId: number, status: 'confirmed' | 'declined' | 'interested') => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) { toast.error('Please login again'); navigate('/'); return; }
      const user = JSON.parse(userData) as MemberData;
      await axios.post('/attendance/rsvp', { memberId: user.id, eventId, rsvpStatus: status });
      const statusLabels = { confirmed: 'Going', declined: 'Not Going', interested: 'Interested' };
      toast.success(`RSVP updated: ${statusLabels[status]}`);
      loadEvents();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to update RSVP');
    }
  };

  const handleViewCertificate = (attendance: AttendanceRecord) => {
    setSelectedCertificate(attendance);
    setShowCertificateModal(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getLevelIcon = (level: string): React.ReactNode => {
    switch (level?.toLowerCase()) {
      case 'platinum': return <Crown className="w-6 h-6" />;
      case 'gold':     return <Trophy className="w-6 h-6" />;
      case 'silver':   return <Star className="w-6 h-6" />;
      default:         return <Award className="w-6 h-6" />;
    }
  };

  const getLevelColor = (level: string): string => {
    switch (level?.toLowerCase()) {
      case 'platinum': return 'from-slate-500 to-slate-700';
      case 'diamond':  return 'from-[#0D5128] to-[#C49A3C]';
      case 'gold':     return 'from-[#C49A3C] to-[#a97f2f]';
      case 'silver':   return 'from-gray-400 to-slate-500';
      default:         return 'from-[#0D5128] to-[#1a7a4a]';
    }
  };

  // ── Loading state ──────────────────────────────────────────────
  if (loading || !member) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#0D5128] border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#0D5128] font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // ── Normalized fields ──────────────────────────────────────────
  const member_fullName     = (member?.full_name || member?.fullName || '') as string;
  const member_memberId     = (member?.member_id || member?.memberId || '') as string;
  const member_level        = (member?.achievement_level || member?.achievementLevel || 'Bronze') as string;
  const member_totalPoints  = (member?.total_points ?? member?.totalPoints ?? 0) as number;
  const member_rankPosition = (member?.rank_position ?? member?.rankPosition ?? null) as number | null;
  const member_profileImage = (member?.profileImage || member?.profile_image) as string | undefined;

  const { greeting, subtitle } = getTimeGreeting(member_fullName);
  const { percent: levelPercent, pointsNeeded, nextLevel } = getProgressToNextLevel(member_totalPoints, member_level);
  const volunteerHours = Math.round(attendanceHistory.length * 3);
  const currentQuote = VOLUNTEER_QUOTES[quoteIndex];

  const buildEventCardProps = (event: Event) => ({
    ...event,
    isRegistered: registeredEvents.includes(event.id),
    hasAttended: Array.isArray(attendanceHistory) ? attendanceHistory.some(a => a.event_id === event.id) : false,
  });

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #f0fdf4 0%, #ecfdf5 50%, #f7fee7 100%)' }}>

      {/* ── Header ── */}
      <header className="bg-[#0D5128] border-b border-[#C49A3C]/30 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="bg-white/10 p-2 rounded-lg">
                <img src="/website-04.png" alt="Hammad Foundation" className="h-12 w-auto object-contain" />
              </div>
            </div>
            <button
              onClick={handleLogout}
              aria-label="Logout"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#C49A3C] hover:bg-[#B8903A] text-white font-medium transition-all"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Hero ── */}
        <div className="mb-6 relative overflow-hidden rounded-3xl shadow-xl" style={{
          background: 'linear-gradient(135deg, #041e0e 0%, #0D5128 55%, #1a6b3c 100%)',
        }}>
          {/* Decorative orbs */}
          <div className="absolute top-0 right-0 w-80 h-80 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(196,154,60,0.18) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
          <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(196,154,60,0.12) 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

          <div className="relative z-10 p-8">
            <div className="flex items-center gap-5 mb-6">
              {member_profileImage ? (
                <img
                  src={member_profileImage}
                  alt={member_fullName}
                  className="w-20 h-20 rounded-full border-4 object-cover shadow-lg flex-shrink-0"
                  style={{ borderColor: 'rgba(196,154,60,0.6)' }}
                />
              ) : (
                <div className="w-20 h-20 rounded-full border-4 flex-shrink-0 flex items-center justify-center text-3xl font-bold shadow-lg"
                  style={{ borderColor: 'rgba(196,154,60,0.6)', background: 'rgba(196,154,60,0.2)' }}>
                  <span className="text-white">{member_fullName.charAt(0)}</span>
                </div>
              )}
              <div>
                <h2 className="text-3xl font-extrabold text-white tracking-tight leading-tight">
                  {greeting}
                </h2>
                <p className="mt-1 text-base" style={{ color: 'rgba(196,154,60,0.9)' }}>{subtitle}</p>
                <p className="mt-1 text-xs font-mono" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  ID: {member_memberId}
                </p>
              </div>
            </div>

            {/* Level progress bar */}
            {nextLevel && (
              <div className="mb-6 p-4 rounded-2xl" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white">{member_level}</span>
                    <ArrowRight size={14} style={{ color: '#C49A3C' }} />
                    <span className="text-sm font-semibold" style={{ color: '#C49A3C' }}>{nextLevel}</span>
                  </div>
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {pointsNeeded} pts to {nextLevel}
                  </span>
                </div>
                <div className="w-full rounded-full h-2.5" style={{ background: 'rgba(255,255,255,0.12)' }}>
                  <div
                    className="h-2.5 rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${levelPercent}%`,
                      background: 'linear-gradient(90deg, #C49A3C, #e5c46a)',
                      boxShadow: '0 0 8px rgba(196,154,60,0.5)',
                    }}
                  />
                </div>
                <p className="mt-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{levelPercent}% of the way there</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/member/check-in')}
                className="px-5 py-2.5 bg-white hover:bg-gray-50 text-[#0D5128] rounded-xl font-semibold transition-all flex items-center gap-2 shadow-lg text-sm"
              >
                <QrCode size={18} />
                Scan QR Check-In
              </button>
              <button
                onClick={() => navigate('/member/events')}
                className="px-5 py-2.5 rounded-xl font-semibold transition-all flex items-center gap-2 shadow-lg text-sm text-white"
                style={{ background: '#C49A3C' }}
              >
                <Sparkles size={18} />
                Browse Events
              </button>
              <button
                onClick={handleRequestCard}
                disabled={voucherLoading}
                className="px-5 py-2.5 rounded-xl font-semibold transition-all flex items-center gap-2 text-sm text-white disabled:opacity-70"
                style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {voucherLoading
                  ? <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                  : <Download size={18} />}
                {voucherUrl ? 'Download Card' : 'Request Card'}
              </button>
              <button
                onClick={() => navigate('/member/profile')}
                className="px-5 py-2.5 rounded-xl font-semibold transition-all flex items-center gap-2 text-sm text-white"
                style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)' }}
              >
                <Settings size={18} />
                <span className="hidden sm:inline">Edit Profile</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Inspirational Quote Banner ── */}
        <div className="mb-6 rounded-3xl overflow-hidden relative"
          style={{
            background: 'linear-gradient(135deg, #0a2e18 0%, #0D5128 60%, #153d20 100%)',
            boxShadow: '0 4px 24px rgba(13,81,40,0.25)',
          }}>
          {/* Left accent bar */}
          <div className="absolute left-0 top-6 bottom-6 w-1 rounded-r-full" style={{ background: '#C49A3C' }} />

          {/* Ambient quote mark */}
          <div className="absolute top-2 right-6 text-[120px] font-serif leading-none pointer-events-none select-none"
            style={{ color: 'rgba(196,154,60,0.07)', fontFamily: 'Georgia, serif' }}>
            "
          </div>

          <div className="relative px-8 py-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {/* Label */}
                <div className="flex items-center gap-2 mb-3">
                  <Heart size={14} style={{ color: '#C49A3C' }} className="flex-shrink-0" />
                  <span className="text-xs font-bold tracking-widest uppercase" style={{ color: 'rgba(196,154,60,0.8)' }}>
                    Daily Inspiration
                  </span>
                </div>

                {/* Quote text with fade animation */}
                <blockquote
                  className="text-lg sm:text-xl font-medium italic leading-relaxed transition-all duration-300"
                  style={{
                    color: 'rgba(255,255,255,0.92)',
                    opacity: quoteVisible ? 1 : 0,
                    transform: quoteVisible ? 'translateY(0)' : 'translateY(6px)',
                  }}
                >
                  "{currentQuote.quote}"
                </blockquote>

                {/* Author */}
                <div
                  className="mt-3 flex items-center gap-2 transition-all duration-300"
                  style={{ opacity: quoteVisible ? 1 : 0, transitionDelay: '80ms' }}
                >
                  <div className="w-6 h-px" style={{ background: '#C49A3C' }} />
                  <div>
                    <span className="text-sm font-semibold" style={{ color: '#C49A3C' }}>{currentQuote.author}</span>
                    <span className="text-xs ml-2" style={{ color: 'rgba(255,255,255,0.4)' }}>{currentQuote.role}</span>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex flex-col gap-2 flex-shrink-0">
                <button
                  onClick={() => handleQuoteNav('prev')}
                  aria-label="Previous quote"
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110"
                  style={{ background: 'rgba(196,154,60,0.15)', border: '1px solid rgba(196,154,60,0.3)' }}
                >
                  <ChevronLeft size={16} style={{ color: '#C49A3C' }} />
                </button>
                <button
                  onClick={() => handleQuoteNav('next')}
                  aria-label="Next quote"
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110"
                  style={{ background: 'rgba(196,154,60,0.15)', border: '1px solid rgba(196,154,60,0.3)' }}
                >
                  <ChevronRight size={16} style={{ color: '#C49A3C' }} />
                </button>
              </div>
            </div>

            {/* Dot indicators */}
            <div className="flex items-center gap-1.5 mt-4">
              {VOLUNTEER_QUOTES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    if (quoteIntervalRef.current) clearInterval(quoteIntervalRef.current);
                    setQuoteVisible(false);
                    setTimeout(() => { setQuoteIndex(i); setQuoteVisible(true); }, 300);
                    quoteIntervalRef.current = setInterval(() => cycleQuote('next'), 7000);
                  }}
                  aria-label={`Quote ${i + 1}`}
                  className="transition-all duration-300 rounded-full"
                  style={{
                    width: i === quoteIndex ? 20 : 6,
                    height: 6,
                    background: i === quoteIndex ? '#C49A3C' : 'rgba(196,154,60,0.3)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── Stats Grid ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<Award className="w-6 h-6 text-white" />}
            gradient={getLevelColor(member_level)}
            emoji="🎯"
            label="Total Points"
            value={member_totalPoints}
            sub={`≈ ${volunteerHours}h volunteer impact`}
            subColor="#0D5128"
          />
          <StatCard
            icon={getLevelIcon(member_level)}
            gradient={getLevelColor(member_level)}
            emoji="✨"
            label="Achievement"
            value={member_level}
            sub="Level up soon!"
            subColor="#888"
            isText
          />
          <StatCard
            icon={<Target className="w-6 h-6 text-white" />}
            gradient="from-[#C49A3C] to-[#a97f2f]"
            emoji="🏆"
            label="Global Rank"
            value={member_rankPosition != null ? member_rankPosition : 0}
            displayValue={member_rankPosition != null ? `#${member_rankPosition}` : '–'}
            sub="Among all members"
            subColor="#888"
          />
          <StatCard
            icon={<Calendar className="w-6 h-6 text-white" />}
            gradient="from-[#0D5128] to-[#1a7a4a]"
            emoji="📅"
            label="Events Attended"
            value={attendanceHistory.length}
            sub={`${attendanceHistory.length > 0 ? 'Amazing participation!' : 'Attend your first event'}`}
            subColor="#888"
          />
        </div>

        {/* ── Mission Video / Foundation Story Banner ── */}
        <div
          className="mb-6 rounded-3xl overflow-hidden cursor-pointer group relative"
          onClick={() => FOUNDATION_VIDEO_ID ? setShowVideoModal(true) : navigate('/member/events')}
          style={{
            background: 'linear-gradient(135deg, #07200f 0%, #0D5128 40%, #1d6b3d 75%, #0a3519 100%)',
            boxShadow: '0 8px 32px rgba(13,81,40,0.4)',
          }}
        >
          {/* Animated shimmer */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style={{ background: 'linear-gradient(105deg, transparent 30%, rgba(196,154,60,0.08) 50%, transparent 70%)' }} />

          {/* Gold orb top-right */}
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(196,154,60,0.2) 0%, transparent 70%)' }} />

          <div className="relative p-7 flex items-center gap-6">
            {/* Play button */}
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
              style={{ background: 'rgba(196,154,60,0.2)', border: '2px solid rgba(196,154,60,0.4)', boxShadow: '0 0 20px rgba(196,154,60,0.2)' }}
            >
              {FOUNDATION_VIDEO_ID
                ? <Play size={28} style={{ color: '#C49A3C' }} className="ml-1" />
                : <Sparkles size={26} style={{ color: '#C49A3C' }} />
              }
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: 'rgba(196,154,60,0.8)' }}>
                Hammad Foundation
              </p>
              <h3 className="text-xl sm:text-2xl font-extrabold text-white leading-tight mb-1">
                {FOUNDATION_VIDEO_ID ? 'Watch Our Story' : 'Our Mission in Action'}
              </h3>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {FOUNDATION_VIDEO_ID
                  ? 'See the impact your service is creating in our communities'
                  : 'Every event you attend creates real change. Browse and join upcoming events.'}
              </p>

              {/* Stat chips */}
              <div className="flex gap-2 mt-3 flex-wrap">
                {[
                  { icon: <Users size={11} />, text: 'Community Impact' },
                  { icon: <Heart size={11} />, text: 'Service & Care' },
                  { icon: <Sparkles size={11} />, text: 'Earn Points' },
                ].map(chip => (
                  <span
                    key={chip.text}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.09)', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.13)' }}
                  >
                    <span style={{ color: '#C49A3C' }}>{chip.icon}</span>
                    {chip.text}
                  </span>
                ))}
              </div>
            </div>

            {/* Arrow */}
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:translate-x-1 transition-transform duration-200"
              style={{ background: '#C49A3C', boxShadow: '0 4px 16px rgba(196,154,60,0.4)' }}
            >
              <ArrowRight size={20} color="white" />
            </div>
          </div>
        </div>

        {/* ── Events Feed Banner ── */}
        <div
          className="mb-8 rounded-3xl overflow-hidden cursor-pointer group"
          onClick={() => navigate('/member/events')}
          style={{
            background: 'linear-gradient(135deg, #041e0e 0%, #0D5128 50%, #1a7a4a 100%)',
            boxShadow: '0 8px 32px rgba(13,81,40,0.35)',
          }}
        >
          <div style={{ position: 'relative', padding: '28px 32px' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E")`, borderRadius: 24, opacity: 0.6, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', right: -40, top: -40, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(196,154,60,0.25) 0%, transparent 70%)', pointerEvents: 'none' }} />

            <div className="relative flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div style={{ width: 56, height: 56, borderRadius: 16, flexShrink: 0, background: 'rgba(196,154,60,0.20)', border: '1.5px solid rgba(196,154,60,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Calendar size={26} color="#C49A3C" />
                </div>
                <div>
                  <p style={{ color: 'rgba(196,154,60,0.85)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
                    Hammad Foundation
                  </p>
                  <h3 style={{ color: 'white', fontSize: 22, fontWeight: 800, lineHeight: 1.15, marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>
                    Events Feed
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.60)', fontSize: 13 }}>
                    Browse upcoming events, RSVP &amp; earn points
                  </p>
                </div>
              </div>
              <div className="group-hover:translate-x-1 transition-transform duration-200" style={{ width: 46, height: 46, borderRadius: 14, flexShrink: 0, background: '#C49A3C', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(196,154,60,0.4)' }}>
                <ArrowRight size={22} color="white" />
              </div>
            </div>

            <div className="relative flex gap-3 mt-5 flex-wrap">
              {[
                { emoji: '📅', text: `${events.filter(e => e.status === 'upcoming').length} Upcoming` },
                { emoji: '🔴', text: `${events.filter(e => e.status === 'ongoing').length} Live Now` },
                { emoji: '⚡', text: 'Earn Points' },
              ].map(chip => (
                <div key={chip.text} style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 999, padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12 }}>{chip.emoji}</span>
                  <span style={{ color: 'rgba(255,255,255,0.80)', fontSize: 12, fontWeight: 600 }}>{chip.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="mb-6">
          <div className="flex gap-2 bg-white rounded-2xl p-2 shadow-lg">
            {(['events', 'achievements', 'certificates'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all capitalize ${
                  activeTab === tab ? 'bg-[#0D5128] text-white shadow-lg' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  {tab === 'events' && <Calendar size={18} />}
                  {tab === 'achievements' && <Trophy size={18} />}
                  {tab === 'certificates' && <Award size={18} />}
                  <span className="hidden sm:inline">{tab}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab: Events ── */}
        {activeTab === 'events' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.length === 0 ? (
              <div className="col-span-3 text-center py-16">
                <Calendar className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No events yet</p>
                <p className="text-gray-400 text-sm">Check back soon for upcoming opportunities</p>
              </div>
            ) : (
              events.map((event) => (
                <EventCard
                  key={event.id}
                  event={buildEventCardProps(event)}
                  onRegister={handleRegisterEvent}
                  onRSVP={handleRSVP}
                />
              ))
            )}
          </div>
        )}

        {/* ── Tab: Achievements ── */}
        {activeTab === 'achievements' && (
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br ${getLevelColor(member_level)} mb-4 shadow-lg`}>
                <div className="text-white scale-150">{getLevelIcon(member_level)}</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{member_level} Member</h3>
              <p className="text-gray-500">You're doing amazing! Keep up the great work.</p>

              {/* Progress to next level */}
              {nextLevel && (
                <div className="mt-6 max-w-md mx-auto">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-semibold text-gray-700">{member_level}</span>
                    <span className="font-semibold" style={{ color: '#C49A3C' }}>{nextLevel}</span>
                  </div>
                  <div className="w-full rounded-full h-3" style={{ background: '#f0fdf4' }}>
                    <div
                      className="h-3 rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: `${levelPercent}%`,
                        background: 'linear-gradient(90deg, #C49A3C, #e5c46a)',
                        boxShadow: '0 0 8px rgba(196,154,60,0.4)',
                      }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-400">
                    {pointsNeeded > 0 ? `${pointsNeeded} more points to reach ${nextLevel}` : `You've reached ${member_level}!`}
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: <Zap className="w-8 h-8 text-[#0D5128] mx-auto mb-2" />, value: member_totalPoints, label: 'Points Earned', bg: 'bg-[#0D5128]/8 border-[#0D5128]/10' },
                { icon: <Calendar className="w-8 h-8 text-[#0D5128] mx-auto mb-2" />, value: attendanceHistory.length, label: 'Events Joined', bg: 'bg-[#0D5128]/8 border-[#0D5128]/10' },
                { icon: <Star className="w-8 h-8 text-[#C49A3C] mx-auto mb-2" />, value: member_rankPosition != null ? `#${member_rankPosition}` : '–', label: 'Global Rank', bg: 'bg-[#C49A3C]/10 border-[#C49A3C]/20', isString: true },
                { icon: <Gift className="w-8 h-8 text-[#C49A3C] mx-auto mb-2" />, value: attendanceHistory.length, label: 'Certificates', bg: 'bg-[#C49A3C]/10 border-[#C49A3C]/20' },
              ].map((item, i) => (
                <div key={i} className={`text-center p-4 rounded-xl border ${item.bg}`}>
                  {item.icon}
                  <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                  <p className="text-sm text-gray-500">{item.label}</p>
                </div>
              ))}
            </div>

            {/* Impact statement */}
            <div className="rounded-2xl p-5 text-center" style={{ background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', border: '1px solid rgba(13,81,40,0.1)' }}>
              <p className="text-sm font-semibold" style={{ color: '#0D5128' }}>
                Your Volunteer Impact
              </p>
              <p className="text-3xl font-extrabold mt-1" style={{ color: '#0D5128' }}>
                ≈ {volunteerHours} hours
              </p>
              <p className="text-xs text-gray-400 mt-1">of estimated community service contributed</p>
            </div>
          </div>
        )}

        {/* ── Tab: Certificates ── */}
        {activeTab === 'certificates' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {attendanceHistory.length > 0 ? (
              attendanceHistory.map((attendance, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                  onClick={() => handleViewCertificate(attendance)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#C49A3C] to-[#a97f2f] rounded-xl group-hover:scale-110 transition-transform">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <Download className="w-5 h-5 text-gray-400 group-hover:text-[#0D5128] transition-colors" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{attendance.event_title}</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    {attendance.event_date ? new Date(attendance.event_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                  </p>
                  <span className="px-3 py-1 bg-[#C49A3C]/15 text-[#a97f2f] text-xs font-semibold rounded-full border border-[#C49A3C]/30">
                    +{attendance.points_earned} Points
                  </span>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center py-16">
                <Award className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No certificates yet</p>
                <p className="text-gray-400 text-sm">Attend events to earn certificates!</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ── Certificate Modal ── */}
      {showCertificateModal && selectedCertificate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Event Certificate</h3>
              <button
                onClick={() => setShowCertificateModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors text-gray-700"
              >
                Close
              </button>
            </div>
            <Certificate
              memberName={member_fullName}
              eventTitle={selectedCertificate.event_title}
              eventDate={selectedCertificate.event_date}
              points={selectedCertificate.points_earned}
              memberId={member_memberId}
              profileImage={member_profileImage}
              certificateTemplate={selectedCertificate.certificate_template}
            />
          </div>
        </div>
      )}

      {/* ── Video Modal ── */}
      {showVideoModal && FOUNDATION_VIDEO_ID && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="w-full max-w-4xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Our Foundation Story</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                aria-label="Close video"
                className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
                style={{ background: 'rgba(255,255,255,0.15)' }}
              >
                <X size={18} color="white" />
              </button>
            </div>
            <div className="rounded-2xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${FOUNDATION_VIDEO_ID}?autoplay=1&rel=0`}
                title="Foundation Mission Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── StatCard sub-component ────────────────────────────────────────
interface StatCardProps {
  icon: React.ReactNode;
  gradient: string;
  emoji: string;
  label: string;
  value: number | string;
  displayValue?: string;
  sub: string;
  subColor: string;
  isText?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, gradient, emoji, label, value, displayValue, sub, subColor, isText }) => {
  const animated = useAnimatedCounter(isText ? 0 : (typeof value === 'number' ? value : 0), 1000);
  const shown = displayValue ?? (isText ? value : animated);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient}`}>{icon}</div>
        <span className="text-xl">{emoji}</span>
      </div>
      <p className="text-gray-500 text-xs mb-1 font-medium">{label}</p>
      <p className="text-2xl font-extrabold text-gray-900 leading-none">{shown}</p>
      <p className="mt-1.5 text-xs" style={{ color: subColor }}>{sub}</p>
    </div>
  );
};

export default MemberDashboardNew;
